import React from 'react';
import { View, StyleSheet } from 'react-native';
import { EligibilityEvaluation } from '@plentifulapp/plentiful-wizard-engine';
import { MaterialIcons } from '@expo/vector-icons';
import { useLocalization } from '../../store/utils/useLocalization';
import Colors from '../../constants/Colors';

import { AppText } from '../AppText';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    alignItems: 'center',
  },
});

interface StateProgramEligibilityModalProps {
  eligibilityEvaluation: EligibilityEvaluation;
  programName: string;
}

type MaterialIconName = React.ComponentProps<typeof MaterialIcons>['name'];

interface EligibilityStatusDisplay {
  iconName: MaterialIconName;
  iconColor: string;
  description: (
    t: (key: string, params?: any) => string,
    programName: string,
    ineligibleReason?: string
  ) => string;
}

function StateProgramEligibilityModalContent(props: StateProgramEligibilityModalProps): React.ReactNode {
  const { locale, t } = useLocalization();

  const ineligibleReason =
    props.eligibilityEvaluation.result === 'ineligible' && props.eligibilityEvaluation.ineligibleReasons
      ? props.eligibilityEvaluation.ineligibleReasons[locale]
      : '';

  // Define all eligibility statuses with their properties
  const eligibilityStatuses: Record<string, EligibilityStatusDisplay> = {
    ineligible: {
      iconName: 'assignment-late',
      iconColor: Colors.errorBackground,
      description: (translate, programName, reason) =>
        translate('program_ineligible', {
          program: programName,
          reason,
        }),
    },
    eligible: {
      iconName: 'assignment-turned-in',
      iconColor: Colors.primaryColor,
      description: (translate, programName) => translate('program_eligible', { program: programName }),
    },
    notApplicable: {
      iconName: 'assignment-turned-in',
      iconColor: Colors.primaryColor,
      description: (translate, programName) => translate('program_completed', { program: programName }),
    },
    unknown: {
      iconName: 'assignment-late',
      iconColor: Colors.warningBackground,
      description: (translate) => translate('program_unknown_eligibility'),
    },
  };

  // Get the eligibility status or default to 'unknown'
  const status = eligibilityStatuses[props.eligibilityEvaluation.result]
    ? eligibilityStatuses[props.eligibilityEvaluation.result]
    : eligibilityStatuses.unknown;

  const eligibilityIconName: MaterialIconName = status.iconName;
  const eligibilityIconColor = status.iconColor;
  const eligibilityDescription = status.description(t, props.programName, ineligibleReason);

  return (
    <View style={styles.container}>
      <MaterialIcons name={eligibilityIconName} color={eligibilityIconColor} size={48} />
      <AppText>{eligibilityDescription}</AppText>
    </View>
  );
}

export default StateProgramEligibilityModalContent;
